<template>
  <div
    class="card"
    style="border-top-left-radius: 0; border-top-right-radius: 0"
  >
    <div class="card-header">
      <div class="row mx-0 align-items-center w-100">
        <h4 class="col px-0 flex align-items-center w-100 mb-0">
          Brand Leaderboard

          <b-spinner
            v-if="isLoading"
            small
            label="Spinning"
            class="ml-50 mb-25"
          />
        </h4>

        <div class="col-auto px-0">
          <button
            class="btn btn-primary d-flex align-items-center"
            :disabled="!merchantLeaderboard.data.length || isLoading"
            @click="exportToCsv"
          >
            <feather-icon
              icon="DownloadIcon"
              size="18"
              class="mr-1"
            />

            Export CSV
          </button>
        </div>
      </div>
    </div>
    <div class="card-body">
      <!-- Filters -->
      <div class="row mb-2">
        <div class="col-md-4">
          <b-form-group label="Dari Tanggal">
            <b-form-datepicker
              :value="startDate"
              :disabled="isLoading"
              @input="value => $store.commit('brandLeaderboard/SET_START_DATE', value)"
              @input.self="handleFilterChange"
            />
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group label="Hingga Tanggal">
            <b-form-datepicker
              :value="endDate"
              :disabled="isLoading"
              @input="value => $store.commit('brandLeaderboard/SET_END_DATE', value)"
              @input.self="handleFilterChange"
            />
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-checkbox
            id="checkbox-1"
            :checked="showAllMerchantLeaderboard"
            name="checkbox-1"
            style="margin-top: 37px"
            :disabled="isLoading"
            @input="value => $store.commit('brandLeaderboard/SET_SHOW_ALL_MERCHANT_LEADERBOARD', value)"
            @input.self="fetchMerchants"
          >
            Tampilkan semua brand <span v-if="pagination.total">({{ pagination.total }})</span>
          </b-form-checkbox>
        </div>
      </div>

      <!-- Merchants Table -->
      <div
        class="table-responsive overflow-auto"
      >
        <table
          class="table table-bordered"
          :class="{'opacity-50': isLoading}"
          style="max-height: 1000px"
        >
          <thead>
            <tr>
              <th
                class="sticky-top"
                style="z-index: 0"
              >
                Rank
              </th>
              <th
                class="sticky-top"
                style="z-index: 0"
              >
                Brand
              </th>
              <th
                class="sticky-top"
                style="z-index: 0"
              >
                Jumlah Transaksi
              </th>
              <th
                class="sticky-top"
                style="z-index: 0"
              >
                Total Penjualan
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(merchant, index) in merchantLeaderboard.data"
              :key="merchant.merchant_id"
            >
              <td>#{{ calculateRank(index) }}</td>
              <td>{{ merchant.name }}</td>
              <td>{{ merchant.transaction_count }}</td>
              <td>{{ formatCurrency(merchant.total_sales) }}</td>
            </tr>
            <tr v-if="!merchantLeaderboard.data.length">
              <td
                colspan="4"
                class="text-center"
              >
                Tidak ada data untuk ditampilkan.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="d-flex justify-content-between align-items-center mt-3 mb-0.5">
        <div>
          Menampilkan {{ pagination.from || 0 }}-{{ calculateTo }} dari {{ pagination.total || 0 }} baris
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="pagination.total"
          :per-page="pagination.per_page"
          :limit="5"
          align="center"
          class="mb-0"
          @change="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  BFormDatepicker,
  BFormCheckbox,
  BPagination,
  BFormGroup,
  BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapState } from 'vuex'

export default {
  components: {
    BFormDatepicker,
    BFormCheckbox,
    BFormGroup,
    BPagination,
    BSpinner,
  },
  data() {
    return {
      currentPage: 1,
      isLoading: false,
    }
  },
  computed: {
    ...mapState('brandLeaderboard', [
      'showAllMerchantLeaderboard',
      'merchantLeaderboard',
      'startDate',
      'endDate',
    ]),
    calculateTo() {
      const to = this.pagination.from + this.merchantLeaderboard.data.length - 1
      return to || 0
    },
    pagination() {
      return {
        ...this.merchantLeaderboard.meta,
        per_page: this.showAllMerchantLeaderboard ? 1000 : 10,
      }
    },
  },
  mounted() {
    if (!this.merchantLeaderboard || !this.merchantLeaderboard.data || !this.merchantLeaderboard.data.length) {
      this.fetchMerchants()
    }

    if (this.merchantLeaderboard.meta.current_page) {
      this.currentPage = this.merchantLeaderboard.meta.current_page
    }
  },
  methods: {
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    async fetchMerchants() {
      const startDate = new Date(this.startDate)
      const endDate = new Date(this.endDate)

      if (startDate > endDate) {
        return
      }

      this.isLoading = true

      try {
        if (this.showAllMerchantLeaderboard) {
          await this.delay(5000)
        } else {
          await this.delay(500)
        }

        const response = await this.$http.get('/v5/report/top-sales-merchants', {
          params: {
            page: this.currentPage,
            limit: this.pagination.per_page,
            start_date: this.startDate,
            end_date: this.endDate,
          },
        })

        this.$store.commit('brandLeaderboard/SET_MERCHANT_LEADERBOARD', response.data)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Gagal memuat data',
            icon: 'BellIcon',
          },
        })
      } finally {
        this.isLoading = false
      }
    },

    // reset other tab data to force reload data from API
    handleFilterChange() {
      // tab 2
      this.$store.commit('brandLeaderboard/RESET_TOTAL_REPORT')
      this.$store.commit('brandLeaderboard/RESET_DAILY_REPORT')
      // tab 3
      this.$store.commit('brandLeaderboard/RESET_TRANSACTION_ITEMS')
      this.fetchMerchants()
    },

    handlePageChange(page) {
      this.currentPage = page
      this.fetchMerchants()
    },

    calculateRank(index) {
      return ((this.pagination.current_page - 1) * this.pagination.per_page) + index + 1
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
      }).format(amount || 0)
    },

    exportToCsv() {
      if (!this.merchantLeaderboard.data.length) return

      const headers = ['rank', 'merchant_id', 'name', 'transaction_count', 'total_sales']

      const csvContent = [
        headers.join(','),
        ...this.merchantLeaderboard.data.map((merchant, index) => {
          const rank = this.calculateRank(index)
          return `"${rank}","${merchant.merchant_id}","${merchant.name}","${merchant.transaction_count}","${merchant.total_sales}"`
        }),
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)

        if (this.showAllMerchantLeaderboard) {
          link.setAttribute('download', `Sales Report Top Brands ${this.startDate}-${this.endDate}.csv`)
        } else {
          link.setAttribute('download', `Sales Report Top Brands ${this.startDate}-${this.endDate} Page-${this.currentPage}.csv`)
        }
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
  },
}
</script>

<style scoped>
.card {
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}
</style>
